import type { BritProtocol, ScreenRole as ApiScreenRole, StudyRole } from '../../../api/types';
import type { ChildWindowState, ScreenRole as MonitorRole } from '../../multi-monitor/types';
import type { OHIFHangingProtocol } from './britToOhif';
import {
  britToOhifProtocolForScreen,
  getProtocolScreenRoles,
  isMultiScreenBritProtocol,
} from './britToOhif';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProtocolAssignment {
  /** The child window this assignment is for */
  windowId: string;
  /** The OHIF hanging protocol to apply on that window */
  ohifProtocol: OHIFHangingProtocol;
  /** Study UID to load (may differ for prior-viewer) */
  studyUID: string;
  /** The backend screen role (READING1, READING2, etc.) used */
  apiScreenRole: ApiScreenRole;
}

// ---------------------------------------------------------------------------
// Monitor role → Backend screen role mapping
// ---------------------------------------------------------------------------

/**
 * Default mapping from multi-monitor logical roles to backend screen roles.
 *
 * The backend protocol system uses READING1–READING8. By convention:
 * - READING1 is the primary reading screen
 * - READING2 is the secondary
 * - READING3+ are additional screens
 *
 * The "prior-viewer" role is special — it shows a comparison study, not
 * a different screen of the current study. We map it to READING2 as a
 * fallback (so it gets a layout) but the caller must substitute the
 * comparison study UID.
 */
const MONITOR_TO_API_SCREEN: Partial<Record<MonitorRole, ApiScreenRole>> = {
  'primary-viewer': 'READING1',
  'secondary-viewer': 'READING2',
  'prior-viewer': 'READING2',
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Distribute a BritProtocol across open child windows.
 *
 * For each child window, determines which backend screen role to use,
 * translates that screen's layout into an OHIF hanging protocol, and
 * returns an assignment for each window that should receive a protocol.
 *
 * Windows whose roles can't be mapped (e.g. 'worklist', 'unassigned')
 * or whose target screen doesn't exist in the protocol are omitted.
 *
 * @param protocol    The matched BritProtocol
 * @param children    Currently open child windows
 * @param studyUID    Current study UID
 * @param priorStudyUID  Optional comparison study UID
 */
export function distributeProtocol(
  protocol: BritProtocol,
  children: ChildWindowState[],
  studyUID: string,
  priorStudyUID?: string,
): ProtocolAssignment[] {
  const assignments: ProtocolAssignment[] = [];
  const availableRoles = new Set(getProtocolScreenRoles(protocol));

  for (const child of children) {
    if (!child.alive) continue;

    const apiRole = resolveApiScreenRole(child.role, availableRoles);
    if (!apiRole) continue;

    const ohifProtocol = britToOhifProtocolForScreen(protocol, apiRole);
    if (!ohifProtocol) continue;

    // Prior-viewer windows load the comparison study
    const uid = child.role === 'prior-viewer' && priorStudyUID
      ? priorStudyUID
      : studyUID;

    assignments.push({
      windowId: child.windowId,
      ohifProtocol,
      studyUID: uid,
      apiScreenRole: apiRole,
    });
  }

  return assignments;
}

/**
 * Check whether distributing a protocol to child windows would be useful.
 * Returns true when both conditions are met:
 * 1. The protocol references multiple backend screens (multi-screen protocol)
 * 2. There are living child windows with mappable roles
 */
export function shouldDistribute(
  protocol: BritProtocol,
  children: ChildWindowState[],
): boolean {
  if (!isMultiScreenBritProtocol(protocol)) return false;
  return children.some(
    (c) => c.alive && MONITOR_TO_API_SCREEN[c.role] !== undefined,
  );
}

/**
 * For protocols that only use READING1 but the user has a prior-viewer
 * window open with a comparison study, return an assignment for the
 * prior window. Uses the same READING1 layout for both current and prior.
 */
export function distributePriorStudy(
  protocol: BritProtocol,
  children: ChildWindowState[],
  priorStudyUID: string,
): ProtocolAssignment | null {
  const priorWindow = children.find(
    (c) => c.alive && c.role === 'prior-viewer',
  );
  if (!priorWindow) return null;

  // Use the primary screen layout (READING1) for the prior too
  const ohifProtocol = britToOhifProtocolForScreen(protocol, 'READING1');
  if (!ohifProtocol) return null;

  // Adjust the protocol ID/name so it's distinguishable
  ohifProtocol.id = `${ohifProtocol.id}__prior`;
  ohifProtocol.name = `${ohifProtocol.name} (Prior)`;

  return {
    windowId: priorWindow.windowId,
    ohifProtocol,
    studyUID: priorStudyUID,
    apiScreenRole: 'READING1',
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Resolve a monitor role to the best available backend screen role.
 * Falls back through a priority list if the default isn't available.
 */
function resolveApiScreenRole(
  monitorRole: MonitorRole,
  availableRoles: Set<ApiScreenRole>,
): ApiScreenRole | null {
  const preferred = MONITOR_TO_API_SCREEN[monitorRole];
  if (!preferred) return null;

  if (availableRoles.has(preferred)) return preferred;

  // For secondary-viewer, try READING3+ as fallback
  if (monitorRole === 'secondary-viewer') {
    const fallbacks: ApiScreenRole[] = [
      'READING3', 'READING4', 'READING5', 'READING6', 'READING7', 'READING8',
    ];
    for (const role of fallbacks) {
      if (availableRoles.has(role)) return role;
    }
  }

  return null;
}

/**
 * Return the StudyRole values referenced by views within a specific
 * screen of a protocol step. Useful for determining if a screen
 * shows comparison studies.
 */
export function getStudyRolesForScreen(
  protocol: BritProtocol,
  apiScreenRole: ApiScreenRole,
): StudyRole[] {
  const roles = new Set<StudyRole>();
  for (const step of protocol.steps) {
    const screen = step.screens[apiScreenRole];
    if (!screen) continue;
    for (const view of screen.views) {
      roles.add(view.role);
    }
  }
  return Array.from(roles);
}
